import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Typography,
  Card,
  Space,
  Button,
  Tag,
  Rate,
  Descriptions,
  Tabs,
  Image,
  Spin,
  Empty,
  Alert
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  StarOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import moment from "moment";
import _get from "lodash/get";

import BackButton from "components/BackButton";
import { ROLES } from "utils/constants";
import {
  getTravelPlaceDetailRequest,
  deleteTravelPlaceRequest,
  restoreTravelPlaceRequest,
} from "providers/TravelPlaceProvider/slice";
import "./styles.less";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const TravelDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail, isLoading } = useSelector((state) => state.travelPlace);
  const roleCurrentUser = useSelector((state) =>
    _get(state, "auth.currentUser.role")
  );

  const isAdminPermission = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(
    roleCurrentUser
  );

  useEffect(() => {
    if (id) {
      dispatch(getTravelPlaceDetailRequest({ id }));
    }
  }, [dispatch, id]);

  const handleEdit = () => {
    navigate(`/travel/${id}/edit`);
  };

  const handleDelete = () => {
    dispatch(deleteTravelPlaceRequest({ _id: id }));
    setTimeout(() => {
      navigate("/travel");
    }, 500);
  };

  const handleRestore = () => {
    dispatch(restoreTravelPlaceRequest({ _id: id }));
    dispatch(getTravelPlaceDetailRequest({ id }));
  };

  const renderWorkingHours = () => {
    if (!detail.workingHours || detail.workingHours.length === 0) {
      return <Text type="secondary">Chưa có thông tin</Text>;
    }

    const days = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7", "Chủ nhật"];

    return (
      <div className="working-hours-list">
        {detail.workingHours.map((day, index) => (
          <div key={index} className="working-hour-item">
            <Text strong>{days[index]}: </Text>
            {day.isClosed ? (
              <Tag color="red">Đóng cửa</Tag>
            ) : (
              <Text>
                {day.openTime} - {day.closeTime}
                {day.isFullDay && <Tag color="blue" style={{ marginLeft: 8 }}>Cả ngày</Tag>}
              </Text>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (isLoading && !detail._id) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (!detail._id) {
    return (
      <div className="travel-detail-wrapper">
        <BackButton title="Chi tiết địa điểm du lịch" />
        <Empty description="Không tìm thấy địa điểm" />
      </div>
    );
  }

  return (
    <div className="travel-detail-wrapper">
      <Row className="travel-detail-header" align="middle" justify="space-between">
        <BackButton title="Chi tiết địa điểm du lịch" />
        {isAdminPermission && (
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEdit}
            >
              Chỉnh sửa
            </Button>
            {detail.isDeleted ? (
              <Button
                icon={<DeleteOutlined />}
                onClick={handleRestore}
                style={{ color: "#52c41a", borderColor: "#52c41a" }}
              >
                Khôi phục
              </Button>
            ) : (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
              >
                Xóa
              </Button>
            )}
          </Space>
        )}
      </Row>

      {detail.isDeleted && (
        <Alert
          message="Địa điểm này đã bị xóa"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card loading={isLoading}>
        <Tabs defaultActiveKey="info">
          <TabPane tab="Thông tin chung" key="info">
            <Row gutter={[24, 24]}>
              <Col span={8}>
                {detail.thumbnail ? (
                  <Image
                    src={detail.thumbnail}
                    alt={detail.placeName}
                    style={{ width: "100%", borderRadius: 8 }}
                  />
                ) : (
                  <div className="no-image-placeholder">
                    <PictureOutlined style={{ fontSize: 48, color: "#bfbfbf" }} />
                    <Text type="secondary">Chưa có hình ảnh</Text>
                  </div>
                )}
              </Col>

              <Col span={16}>
                <Descriptions title={detail.placeName} column={1} bordered>
                  <Descriptions.Item label="ID">
                    #{detail.travelPlaceId || "N/A"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Loại du lịch">
                    <Tag color="blue">{detail.travelType?.title || "N/A"}</Tag>
                  </Descriptions.Item>

                  <Descriptions.Item label="Địa chỉ">
                    <Space>
                      <EnvironmentOutlined />
                      {detail.address}
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Điện thoại">
                    <Space>
                      <PhoneOutlined />
                      {detail.phone || "Chưa có"}
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Website">
                    {detail.website ? (
                      <a href={detail.website} target="_blank" rel="noopener noreferrer">
                        <Space>
                          <GlobalOutlined />
                          {detail.website}
                        </Space>
                      </a>
                    ) : (
                      "Chưa có"
                    )}
                  </Descriptions.Item>

                  <Descriptions.Item label="Đánh giá">
                    <Space direction="vertical">
                      <Rate disabled value={detail.rating} allowHalf />
                      <Text type="secondary">
                        {detail.rating}/5 ({detail.totalNumberOfRatings} lượt đánh giá)
                      </Text>
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Mô tả">
                    {detail.description || <Text type="secondary">Chưa có mô tả</Text>}
                  </Descriptions.Item>

                  <Descriptions.Item label="Ngày tạo">
                    {detail.createdAt ? moment(detail.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Giờ làm việc" key="workingHours">
            <Card title="Thời gian hoạt động" extra={<ClockCircleOutlined />}>
              {renderWorkingHours()}
            </Card>
          </TabPane>

          <TabPane tab="Hình ảnh" key="gallery">
            <Card title="Thư viện ảnh">
              {detail.images && detail.images.length > 0 ? (
                <Image.PreviewGroup>
                  <Row gutter={[16, 16]}>
                    {detail.images.map((image, index) => (
                      <Col key={index} xs={12} sm={8} md={6} lg={4}>
                        <Image
                          src={image}
                          alt={`${detail.placeName} - ${index + 1}`}
                          style={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 4 }}
                        />
                      </Col>
                    ))}
                  </Row>
                </Image.PreviewGroup>
              ) : (
                <Empty description="Chưa có hình ảnh" />
              )}
            </Card>
          </TabPane>

          <TabPane tab="Vị trí" key="location">
            <Card title="Thông tin vị trí">
              <Descriptions bordered column={2}>
                <Descriptions.Item label="Vĩ độ">
                  {detail.location?.coordinates?.[1] || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Kinh độ">
                  {detail.location?.coordinates?.[0] || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Khoảng cách" span={2}>
                  {detail.distance ? `${detail.distance.toFixed(2)} km` : "N/A"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default TravelDetail;